import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import ReusableTable from "../../../../shared/ReusableTable/ReusableTable";
import basestyle from "../../../CSS/Base.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import * as constants from "../../../../constants";
import { getAutoDebitListDetails } from "../../services/AutoDebit/AutoDebitServices";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../../StyledComponents/Container";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import { useTranslation } from "react-i18next";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import CustomTextField from "../../../../shared/CustomTextFeild";
import { resetTableState, setPagination, setTableFilters } from "../../../../store/tableSlice";
import { notifyUser } from "../../Utils/notifyUser";
import { getClientDetailsByClientName } from "../../Utils/ClientSettingsTable";

interface Column {
    id: string;
    label: string;
    minWidth: number | string;
    align?: "center" | "left" | "right";
    isHyperlink: boolean;
}

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const AutoDebitSummaryListing = () => {
    const userInfo = useSelector((state: RootState) => state.auth);
    const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const dispatch = useDispatch();
    const filters = useSelector((state: RootState) => state.table.autoDebit);
    const restrictedRoles = [
        ...(ClientDetails?.name !== constants.CLIENT_NAME_DC_GARY ? [constants.ROLES_FINANCIAL_ANALYST] : []),
        constants.ROLES_READ_ONLY_ALL_LOCATIONS,
        constants.ROLES_READ_ONLY_USER,
        constants.ROLES_PROJECT_APPROVER
    ];
    const [page, setPage] = useState(filters?.page || 1);
    const [rowsPerPage, setRowsPerPage] = useState(filters?.pageSize || 5);
    const [listData, setListData] = useState([]);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [loanNumber, setLoanNumber] = useState(filters?.filters.loanNum || "");

    const handlePageChange = (newPage: number) => {
        setPage(newPage + 1);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    };

    const handleIdClick = (rowData: any) => {
        navigate("/autoDebitScheduleRule", { state: rowData });
    };

    const handleSearch = () => {
        setPage(1);
        getAutoDebitListInfo(loanNumber, 1, rowsPerPage);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };


    const handleClear = () => {
        setPage(1);
        setRowsPerPage(5);
        setLoanNumber("");
        getAutoDebitListInfo("", 1, rowsPerPage);
        dispatch(resetTableState({ tableKey: 'autoDebit' }));
    };


    async function getAutoDebitListInfo(loanNum: string, page: number, rowsPerPage: number) {
        setIsLoading(true);
        try {
            const AutoDebitVo = {
                participantId: userInfo?.defaultParticipantId,
                isActive: constants.IS_ACTIVE_YES,
                loanNumber: loanNum,
                page: page,
                size: rowsPerPage,
            };
            dispatch(setTableFilters({ tableKey: 'autoDebit', filters: { loanNum } }));
            dispatch(setPagination({ tableKey: 'autoDebit', page: AutoDebitVo.page, pageSize: rowsPerPage }));
            const responseList = await getAutoDebitListDetails(AutoDebitVo);

            if (responseList.status === constants.STATUS_CODES.SUCCESS) {
                setCount(responseList.data.totalRecordCount);
                setListData(
                    responseList.data.data.map((item: any) => ({
                        ...item,
                        loanDueDate: item.loanDueDate ? formatDate(item.loanDueDate) : "-",
                        autoDebitDate: item.autoDebitDate ? formatDate(item.autoDebitDate) : "-",
                        payment: item.payment === 'statementDue' ? 'Statement Due' : '-',
                    }))
                );
            } else {
                setCount(0);
                setListData([]);
            }
        } catch (error) {
            console.error("Error fetching AutoDebit data:", error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getAutoDebitListInfo(loanNumber || "", page, rowsPerPage);
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (location.state?.message) {
            notifyUser(location.state.message, 'success', translate("autoDebitSummary"));
            navigate(location.pathname, { replace: true });
        }
    }, [location, navigate]);

    const Columns: Column[] = [
        { id: "autoDebitId", label: translate('adRefNo') + "#", minWidth: "100px", align: "center", isHyperlink: true },
        { id: "loanNumber", label: translate('loanNumber'), minWidth: "100px", align: "left", isHyperlink: false },
        { id: "loanDescription", label: translate('loanDesc'), minWidth: "180px", isHyperlink: false },
        { id: "payment", label: translate('payment'), minWidth: "100px", isHyperlink: false },
        { id: "loanDueDate", label: translate('dueDate'), minWidth: "80px", align: "center", isHyperlink: false },
        { id: "autoDebitDate", label: translate('autoPaymentDate'), minWidth: "150px", align: "center", isHyperlink: false },
        { id: "frequency", label: translate('schedule'), minWidth: "150px", isHyperlink: false },
        { id: "status", label: translate('status'), minWidth: "100px", align: "center", isHyperlink: false },
    ];

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('autoDebitSummary')}</SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        <div className="search-fields-container">
                            <div className="left-fields">
                                <CustomTextField
                                    sx={{ width: { xs: "100%", sm: 250 } }}
                                    label={translate('loanNumber')}
                                    value={loanNumber}
                                    onKeyDown={handleKeyPress}
                                    onChange={(e) => setLoanNumber(e.target.value.trim())}
                                    inputProps={{
                                        maxLength: 15
                                    }}
                                />
                                <ContainedButton onClick={handleSearch}> {translate('search')} </ContainedButton>
                                <OutlinedButton onClick={handleClear}> {translate('clear')} </OutlinedButton>
                            </div>
                            <div className="right-fields">
                                <ContainedButton onClick={() => navigate("/autoDebitLoanDetails")}
                                    disabled={restrictedRoles.includes(userInfo?.roles?.roleName ?? '')}>
                                    <ControlPointIcon />{translate('newAutoDebit')}
                                </ContainedButton>
                            </div>
                        </div>
                        <ReusableTable
                            columns={Columns}
                            rows={listData}
                            count={count}
                            page={page - 1}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            onRowClick={handleIdClick}
                            loading={isLoading}
                        />
                        <div className={basestyle.buttoncontainer}>
                            <ContainedButton onClick={() => navigate("/summary")}>
                                {translate('back')}
                            </ContainedButton>
                        </div>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer >
    );
};

export default AutoDebitSummaryListing;
